import React, { useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Divider,
  Button,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../../assets/Logofile/logo (2).png";
import PdfFile from "./PdfFile";

const statusColorMap = {
  approved: "#4caf50",
  pending: "#ff9800",
  rejected: "#f44336",
  active: "#22c55e",
  inactive: "#facc15",
  deleted: "#ef4444",
};

function formatIST(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

const ViewDataPopup = ({ open, onClose, data }) => {
  const pdfRef = useRef();

  const renderSection = (title, fields) => (
    <Box sx={{ marginBottom: 3 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: "#3A6FF8",
          mb: 1,
          fontSize: "1.1rem",
          borderBottom: "2px solid #e3e8f7",
          pb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Table size="small" sx={{ minWidth: 320 }}>
        <TableBody>
          {fields.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#64748b",
                  border: "none",
                  width: 140,
                  py: 1.2,
                  fontSize: "0.9rem",
                }}
              >
                {label}
              </TableCell>
              <TableCell
                sx={{
                  border: "none",
                  py: 1.2,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                {value || <span style={{ color: "#b0b6c3" }}>-</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const personalFields = [
    ["Full Name", data.fullName],
    ["Gender", data.gender],
    ["Date of Birth", data.dateOfBirth ? formatIST(data.dateOfBirth) : "-"],
    ["Mobile Number", data.mobileNumber],
    ["Email", data.emailId],
    ["Address", data.address],
    ["Blood Group", data.bloodGroup],
    [
      "Status",
      <Chip
        label={data.status}
        sx={{
          fontWeight: 600,
          color: "#fff",
          background: statusColorMap[data.status] || "#6366f1",
          fontSize: 14,
          px: 1.5,
          textTransform: "capitalize",
          letterSpacing: 0.5,
        }}
      />,
    ],
    [
      "Application Date",
      data.applicationDate ? formatIST(data.applicationDate) : "-",
    ],
    ["Created At", data.createdAt ? formatIST(data.createdAt) : "-"],
    ["Updated At", data.updatedAt ? formatIST(data.updatedAt) : "-"],
  ];

  const emergencyFields = [
    ["Name", data.emergencyContact?.name],
    ["Mobile", data.emergencyContact?.mobile],
    ["Relation", data.emergencyContact?.relation],
  ];

  const notifyFields = [
    ["Name", data.memberToNotify?.name],
    ["Mobile", data.memberToNotify?.mobile],
    ["Relation", data.memberToNotify?.relation],
  ];

  const motivationFields = [
    ["NGO Name", data.motivation?.ngoName],
    ["Type", data.motivation?.type],
  ];

  const organs = Object.entries(data.organs || {})
    .filter(([k, v]) => v)
    .map(([k]) => k)
    .join(", ");

  const tissues = Object.entries(data.tissues || {})
    .filter(([k, v]) => v)
    .map(([k]) => k)
    .join(", ");

  const handleDownloadPDF = async () => {
    const pdfElement = pdfRef.current;

    // Set the PDF element to A4 dimensions
    pdfElement.style.width = "210mm";
    pdfElement.style.height = "297mm";
    pdfElement.style.padding = "0";
    pdfElement.style.margin = "0";

    // Use html2canvas with cropping options
    const canvas = await html2canvas(pdfElement, {
      scale: 3,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      allowTaint: true,
      imageTimeout: 15000,
      ignoreElements: (element) => {
        // Ignore elements that might cause white space
        return element.classList.contains("ignore-for-pdf");
      },
      onclone: (clonedDoc) => {
        // Ensure the cloned element has proper dimensions
        const element = clonedDoc.querySelector("#pdfContent");
        if (element) {
          element.style.width = "210mm";
          element.style.height = "297mm";
          element.style.padding = "0";
          element.style.margin = "0";
        }
      },
    });

    // Create a temporary canvas to remove white space
    const ctx = canvas.getContext("2d");
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const bounds = {
      top: null,
      left: null,
      right: null,
      bottom: null,
    };

    // Find non-white pixels to determine content bounds
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const pos = (y * canvas.width + x) * 4;
        if (
          pixels.data[pos] !== 255 ||
          pixels.data[pos + 1] !== 255 ||
          pixels.data[pos + 2] !== 255
        ) {
          if (bounds.top === null || y < bounds.top) bounds.top = y;
          if (bounds.bottom === null || y > bounds.bottom) bounds.bottom = y;
          if (bounds.left === null || x < bounds.left) bounds.left = x;
          if (bounds.right === null || x > bounds.right) bounds.right = x;
        }
      }
    }

    // Add some padding to the bounds
    const padding = 20;
    bounds.top = Math.max(0, bounds.top - padding);
    bounds.left = Math.max(0, bounds.left - padding);
    bounds.bottom = Math.min(canvas.height, bounds.bottom + padding);
    bounds.right = Math.min(canvas.width, bounds.right + padding);

    // Calculate width and height of the content area
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;

    // Create a new canvas with just the content area
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = width;
    croppedCanvas.height = height;
    const croppedCtx = croppedCanvas.getContext("2d");
    croppedCtx.drawImage(
      canvas,
      bounds.left,
      bounds.top,
      width,
      height,
      0,
      0,
      width,
      height
    );

    // Create PDF with A4 dimensions
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    // A4 dimensions in pixels at 72 DPI
    const a4Width = 210 * 3.78; // 210mm in pixels at 300 DPI
    const a4Height = 297 * 3.78; // 297mm in pixels at 300 DPI

    // Calculate scaling to fit the cropped content to A4
    const scale = Math.min(
      a4Width / croppedCanvas.width,
      a4Height / croppedCanvas.height
    );

    const scaledWidth = croppedCanvas.width * scale;
    const scaledHeight = croppedCanvas.height * scale;

    // Center the content on the page
    const x = (a4Width - scaledWidth) / 2;
    const y = (a4Height - scaledHeight) / 2;

    // Add image to PDF
    pdf.addImage(
      croppedCanvas.toDataURL("image/png"),
      "PNG",
      x / 3.78, // Convert back to mm
      y / 3.78, // Convert back to mm
      scaledWidth / 3.78, // Convert back to mm
      scaledHeight / 3.78, // Convert back to mm
      undefined,
      "FAST"
    );

    // Save the PDF
    pdf.save(`${data.fullName || "donor"}_certificate.pdf`);

    // Reset the PDF element styles
    pdfElement.style.width = "";
    pdfElement.style.height = "";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          overflow: "visible",
          boxShadow: 8,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 22,
          color: "#1e293b",
          pb: 1,
          pr: 5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "linear-gradient(90deg, #f8fafc, #e3e8f7)",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <InfoOutlinedIcon color="primary" sx={{ fontSize: 28, mr: 1 }} />
        Donor Application Details
        <IconButton
          aria-label="download"
          onClick={handleDownloadPDF}
          sx={{
            ml: "auto",
            color: "#3A6FF8",
            "&:hover": {
              backgroundColor: "rgba(58, 111, 248, 0.1)",
            },
          }}
        >
          <DownloadForOfflineIcon fontSize="large" />
        </IconButton>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "#64748b",
            ml: 1,
            "&:hover": {
              backgroundColor: "rgba(100, 116, 139, 0.1)",
            },
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          py: 3,
          px: { xs: 2, sm: 4 },
          overflowY: "auto",
          background: "#f8fafc",
        }}
      >
        {/* Preview of how the PDF will look */}
        <Box
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            border: "1px solid #e2e8f0",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "#3A6FF8",
              mb: 3,
              fontWeight: 700,
            }}
          >
            DONOR CERTIFICATE
          </Typography>

          {data.image && (
            <Box
              sx={{
                textAlign: "right",
                mb: 2,
                position: "relative",
                top: -70,
              }}
            >
              <img
                src={data.image}
                alt="Donor"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid #e3e8f7",
                }}
              />
            </Box>
          )}

          {/* Card-style grouped sections */}
          {renderSection("Personal Information", personalFields)}
          {renderSection("Emergency Contact", emergencyFields)}
          {renderSection("Member To Notify", notifyFields)}
          {renderSection("Motivation", motivationFields)}
          {renderSection("Organs & Tissues", [
            [
              "Organs Pledged",
              organs || <span style={{ color: "#b0b6c3" }}>-</span>,
            ],
            [
              "Tissues Pledged",
              tissues || <span style={{ color: "#b0b6c3" }}>-</span>,
            ],
          ])}

          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: "1px dashed #e2e8f0",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8rem",
                color: "#64748b",
                fontStyle: "italic",
                mb: 2,
              }}
            >
              This is a preview of how the PDF certificate will look. Click the
              download button above to generate the official certificate.
            </Typography>
          </Box>
        </Box>
        <div style={{ position: "absolute", left: -9999, top: 0 }}>
          <PdfFile ref={pdfRef} data={data} />
        </div>
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          background: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            minWidth: 100,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDataPopup;
